import type { OpinionCitationLink } from '@/citator/schema/opinion-citation-link-schema'
import { kProviderCitationHintAttribute, type ProviderCitationHint } from './sanitize-and-highlight-html'

export type CaseCitationLinkTarget = {
  descriptor: OpinionCitationLink
  opinionUrl: string
  citationAnalysisUrl?: string
}

export type AnnotatedCaseCitation = CaseCitationLinkTarget & {
  occurrenceId: string
  /** Captured for future pinpoint navigation; current URLs intentionally ignore it. */
  pinpoint?: string
}

type TextSegment = {
  node: Text
  start: number
  end: number
  block: Element
}

type NormalizedText = {
  text: string
  rawStarts: number[]
  rawEnds: number[]
}

type Match = {
  start: number
  end: number
  target: CaseCitationLinkTarget
  pinpoint?: string
  block: Element
  providerElement?: Element
}

type MatchCandidate = Omit<Match, 'block'>

// Block-level members of kSanitizationAllowedTags. A citation may span inline
// descendants of one block, but never structural boundaries between these elements.
const kSanitizedBlockTags = new Set([
  'address',
  'article',
  'aside',
  'blockquote',
  'caption',
  'dd',
  'div',
  'dl',
  'dt',
  'figcaption',
  'figure',
  'footer',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'header',
  'hgroup',
  'li',
  'main',
  'nav',
  'ol',
  'p',
  'pre',
  'section',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'ul',
])

/**
 * Add frontend-owned citation anchors to already-sanitized opinion HTML.
 * Matching uses the rendered text stream and DOM ranges, so inline formatting survives.
 */
export function annotateCaseCitationLinks(
  sanitizedHtml: string,
  targets: CaseCitationLinkTarget[],
  providerCitationHints: Map<string, ProviderCitationHint> = new Map()
) {
  const parser = new DOMParser()
  const document = parser.parseFromString(sanitizedHtml, 'text/html')
  const { rawText, segments, segmentByNode } = collectTextSegments(document.body)
  const normalizedDocument = normalizeWithOffsets(rawText)
  const unambiguousTargets = selectTargetsBySource(document, targets)
  const providerMatches = findProviderLocatorMatches(document, segmentByNode, normalizedDocument, targets, providerCitationHints)
  const exactMatches = findExactMatchCandidates(normalizedDocument.text, unambiguousTargets)
  const directMatches = attachBlocksAndSelectMatches([...providerMatches, ...exactMatches], segments, normalizedDocument)
  const matches = mergeAdjacentParallelMatches(directMatches, normalizedDocument.text)
  const citations = new Map<string, AnnotatedCaseCitation>()
  const matchesByBlock = new Map<Element, Array<{ match: Match; occurrenceNumber: number }>>()
  matches.forEach((match, index) => {
    const blockMatches = matchesByBlock.get(match.block) ?? []
    blockMatches.push({ match, occurrenceNumber: index + 1 })
    matchesByBlock.set(match.block, blockMatches)
  })

  for (const [block, blockMatches] of matchesByBlock) {
    const placeholder = block.localName === 'body' || !block.parentNode ? undefined : document.createComment('citation-block')
    if (placeholder) block.replaceWith(placeholder)

    blockMatches.sort((left, right) => right.match.start - left.match.start)
    for (const { match, occurrenceNumber } of blockMatches) {
      const rawStart = normalizedDocument.rawStarts[match.start]
      const rawEnd = normalizedDocument.rawEnds[match.end - 1]
      const start = locateTextPosition(segments, rawStart, false)
      const end = locateTextPosition(segments, rawEnd, true)
      if (!start || !end || start.block !== end.block) continue

      const occurrenceId = `${match.target.descriptor.citation_mapping_id}-${occurrenceNumber}`
      const anchor = document.createElement('a')
      anchor.href = match.target.opinionUrl
      anchor.target = '_blank'
      anchor.rel = 'noopener noreferrer'
      anchor.className = 'text-brand-700 underline decoration-brand-700/40 underline-offset-2 hover:text-brand-800 hover:decoration-brand-800'
      anchor.dataset.caseCitationOccurrence = occurrenceId
      wrapMatchContents(document, anchor, start, end, match.providerElement)

      citations.set(occurrenceId, { ...match.target, occurrenceId, pinpoint: match.pinpoint })
    }

    if (placeholder) placeholder.replaceWith(block)
  }

  document.querySelectorAll(`[${kProviderCitationHintAttribute}]`).forEach((element) => element.removeAttribute(kProviderCitationHintAttribute))

  return { html: document.body.innerHTML, citations }
}

function wrapMatchContents(
  document: Document,
  anchor: HTMLAnchorElement,
  start: { node: Text; offset: number },
  end: { node: Text; offset: number },
  providerElement?: Element
) {
  if (providerElement?.parentNode) {
    const contents = document.createDocumentFragment()
    contents.append(...providerElement.childNodes)
    anchor.append(contents)
    providerElement.replaceWith(anchor)
    return
  }

  if (start.node === end.node) {
    const matchedText = start.node.splitText(start.offset)
    const matchLength = end.offset - start.offset
    if (matchLength < matchedText.length) matchedText.splitText(matchLength)
    matchedText.parentNode?.insertBefore(anchor, matchedText)
    anchor.append(matchedText)
    return
  }

  const range = document.createRange()
  range.setStart(start.node, start.offset)
  range.setEnd(end.node, end.offset)
  anchor.append(range.extractContents())
  range.insertNode(anchor)
}

function collectTextSegments(root: HTMLElement): {
  rawText: string
  segments: TextSegment[]
  segmentByNode: Map<Text, TextSegment>
} {
  const walker = root.ownerDocument.createTreeWalker(root, 4)
  const segments: TextSegment[] = []
  const segmentByNode = new Map<Text, TextSegment>()
  let rawText = ''
  let node = walker.nextNode()

  while (node) {
    const textNode = node as Text
    const value = textNode.data
    if (value.length > 0) {
      const segment = {
        node: textNode,
        start: rawText.length,
        end: rawText.length + value.length,
        block: nearestSanitizedBlockAncestor(textNode),
      }
      segments.push(segment)
      segmentByNode.set(textNode, segment)
    }
    rawText += value
    node = walker.nextNode()
  }

  return { rawText, segments, segmentByNode }
}

function selectTargetsBySource(document: Document, targets: CaseCitationLinkTarget[]): Array<CaseCitationLinkTarget & { normalizedSource: string }> {
  const bySource = new Map<string, CaseCitationLinkTarget[]>()

  for (const target of targets) {
    const decodedSource = decodeEntities(document, target.descriptor.citation_text)
    const normalizedSource = normalizeWithOffsets(decodedSource).text.trim()
    // An Id./Ibid. mapping has no document-wide identity; only a mapped provider
    // occurrence may link it. Never apply that text to every occurrence in an opinion.
    if (!normalizedSource || !target.opinionUrl || /^(?:id\.|ibid\.)(?:$|[\s,])/iu.test(normalizedSource)) continue
    const existing = bySource.get(normalizedSource) ?? []
    existing.push(target)
    bySource.set(normalizedSource, existing)
  }

  const unambiguousTargets: Array<CaseCitationLinkTarget & { normalizedSource: string }> = []
  for (const [normalizedSource, sourceTargets] of bySource) {
    const destinationKeys = new Set(sourceTargets.map(destinationKey))
    if (destinationKeys.size !== 1) {
      continue
    }
    unambiguousTargets.push({ ...sourceTargets[0], normalizedSource })
  }
  return unambiguousTargets
}

function destinationKey(target: CaseCitationLinkTarget): string {
  return `${target.descriptor.document_type}:${target.descriptor.s3_path}`
}

function decodeEntities(document: Document, value: string): string {
  const textarea = document.createElement('textarea')
  textarea.innerHTML = value
  return textarea.value
}

function normalizeWithOffsets(value: string): NormalizedText {
  let text = ''
  const rawStarts: number[] = []
  const rawEnds: number[] = []
  let rawIndex = 0
  let previousWasWhitespace = false

  for (const character of value) {
    const characterLength = character.length
    const normalizedCharacter = normalizeCharacter(character)
    const isWhitespace = /^\s$/u.test(normalizedCharacter)

    if (isWhitespace && previousWasWhitespace) {
      if (rawEnds.length > 0) rawEnds[rawEnds.length - 1] = rawIndex + characterLength
      rawIndex += characterLength
      continue
    }

    const normalizedOutput = isWhitespace ? ' ' : normalizedCharacter
    text += normalizedOutput
    for (let codeUnitIndex = 0; codeUnitIndex < normalizedOutput.length; codeUnitIndex += 1) {
      rawStarts.push(rawIndex + codeUnitIndex)
      rawEnds.push(rawIndex + codeUnitIndex + 1)
    }
    previousWasWhitespace = isWhitespace
    rawIndex += characterLength
  }

  return { text, rawStarts, rawEnds }
}

function normalizeCharacter(character: string): string {
  if (/^[\u2018\u2019\u201A\u201B]$/u.test(character)) return "'"
  if (/^[\u201C\u201D\u201E\u201F]$/u.test(character)) return '"'
  if (/^[\u2010-\u2015\u2212]$/u.test(character)) return '-'
  if (/^\s$/u.test(character)) return ' '
  return character
}

function findExactMatchCandidates(documentText: string, targets: Array<CaseCitationLinkTarget & { normalizedSource: string }>): MatchCandidate[] {
  const candidates: MatchCandidate[] = []
  for (const target of targets) {
    let start = documentText.indexOf(target.normalizedSource)
    while (start !== -1) {
      const end = start + target.normalizedSource.length
      if (hasAlphanumericBoundaries(documentText, target.normalizedSource, start, end)) {
        candidates.push({ start, end, target })
      }
      start = documentText.indexOf(target.normalizedSource, start + 1)
    }
  }

  return candidates
}

function findProviderLocatorMatches(
  document: Document,
  segmentByNode: Map<Text, TextSegment>,
  normalizedDocument: NormalizedText,
  targets: CaseCitationLinkTarget[],
  providerCitationHints: Map<string, ProviderCitationHint>
): MatchCandidate[] {
  const targetsByLocator = new Map<string, CaseCitationLinkTarget[]>()
  for (const target of targets) {
    const locator = target.descriptor.provider_locator
    if (!locator || !target.opinionUrl) continue
    const existing = targetsByLocator.get(locator) ?? []
    existing.push(target)
    targetsByLocator.set(locator, existing)
  }

  const matches: MatchCandidate[] = []
  for (const element of document.querySelectorAll(`[${kProviderCitationHintAttribute}]`)) {
    const hintId = element.getAttribute(kProviderCitationHintAttribute)
    const hint = hintId ? providerCitationHints.get(hintId) : undefined
    const range = normalizedRangeForElement(element, segmentByNode, normalizedDocument)
    if (!hint || !range) continue

    const locatorTargets = targetsByLocator.get(hint.locator) ?? []
    const destinationKeys = new Set(locatorTargets.map(destinationKey))
    if (destinationKeys.size === 1) {
      matches.push({ ...range, target: locatorTargets[0], pinpoint: hint.pinpoint, providerElement: element })
    }
  }

  return matches
}

function normalizedRangeForElement(
  element: Element,
  segmentByNode: Map<Text, TextSegment>,
  normalizedDocument: NormalizedText
): { start: number; end: number } | null {
  const walker = element.ownerDocument.createTreeWalker(element, 4)
  let firstSegment: TextSegment | undefined
  let lastSegment: TextSegment | undefined
  let node = walker.nextNode()
  while (node) {
    const segment = segmentByNode.get(node as Text)
    if (segment) {
      firstSegment ??= segment
      lastSegment = segment
    }
    node = walker.nextNode()
  }
  if (!firstSegment || !lastSegment) return null

  const rawStart = firstSegment.start
  const rawEnd = lastSegment.end
  const start = lowerBound(normalizedDocument.rawStarts, rawStart)
  const end = lowerBound(normalizedDocument.rawStarts, rawEnd)
  return end > start ? { start, end } : null
}

function extractReporterCitations(text: string): Array<{ start: number; end: number; identity: string }> {
  const citations: Array<{ start: number; end: number; identity: string }> = []
  const pattern = /\b(\d+)\s+([A-Za-z][A-Za-z0-9.]*(?:\s+[A-Za-z0-9][A-Za-z0-9.]*){0,3}?)\s+(\d+)\b/gu
  for (const match of text.matchAll(pattern)) {
    if (match.index === undefined || !match[2].includes('.')) continue
    citations.push({ start: match.index, end: match.index + match[0].length, identity: `${match[1]} ${match[2]}` })
  }
  return citations
}

function attachBlocksAndSelectMatches(candidates: MatchCandidate[], segments: TextSegment[], normalizedDocument: NormalizedText): Match[] {
  const matches = candidates
    .map((candidate): Match | null => {
      const block = blockForNormalizedRange(candidate.start, candidate.end, segments, normalizedDocument)
      return block ? { ...candidate, block } : null
    })
    .filter((candidate): candidate is Match => candidate !== null)

  matches.sort((left, right) => right.end - right.start - (left.end - left.start) || left.start - right.start)
  const selected: Match[] = []
  for (const candidate of matches) {
    if (selected.some((match) => candidate.start < match.end && candidate.end > match.start)) continue
    selected.push(candidate)
  }
  return selected
}

function mergeAdjacentParallelMatches(matches: Match[], documentText: string): Match[] {
  const ordered = [...matches].sort((left, right) => left.start - right.start)
  const merged: Match[] = []

  for (const match of ordered) {
    const previous = merged[merged.length - 1]
    if (
      previous &&
      previous.block === match.block &&
      destinationKey(previous.target) === destinationKey(match.target) &&
      /^\s*,\s*$/u.test(documentText.slice(previous.end, match.start)) &&
      extractReporterCitations(documentText.slice(previous.start, previous.end)).length > 0 &&
      extractReporterCitations(documentText.slice(match.start, match.end)).length > 0
    ) {
      previous.end = match.end
      previous.pinpoint = match.pinpoint ?? previous.pinpoint
      previous.providerElement = undefined
      continue
    }
    merged.push({ ...match })
  }

  return merged
}

function blockForNormalizedRange(start: number, end: number, segments: TextSegment[], normalizedDocument: NormalizedText): Element | null {
  const rawStart = normalizedDocument.rawStarts[start]
  const rawEnd = normalizedDocument.rawEnds[end - 1]
  const startPosition = locateTextPosition(segments, rawStart, false)
  const endPosition = locateTextPosition(segments, rawEnd, true)
  if (!startPosition || !endPosition) return null
  return startPosition.block === endPosition.block ? startPosition.block : null
}

function hasAlphanumericBoundaries(documentText: string, sourceText: string, start: number, end: number): boolean {
  const sourceStartsWithAlphanumeric = /^[\p{L}\p{N}]/u.test(sourceText)
  const sourceEndsWithAlphanumeric = /[\p{L}\p{N}]$/u.test(sourceText)
  const prefixEndsWithAlphanumeric = /[\p{L}\p{N}]$/u.test(documentText.slice(0, start))
  const suffixStartsWithAlphanumeric = /^[\p{L}\p{N}]/u.test(documentText.slice(end))

  return !(sourceStartsWithAlphanumeric && prefixEndsWithAlphanumeric) && !(sourceEndsWithAlphanumeric && suffixStartsWithAlphanumeric)
}

function locateTextPosition(segments: TextSegment[], rawOffset: number, isEnd: boolean): { node: Text; offset: number; block: Element } | null {
  let low = 0
  let high = segments.length
  while (low < high) {
    const middle = Math.floor((low + high) / 2)
    const endsAtOrAfterOffset = isEnd ? segments[middle].end >= rawOffset : segments[middle].end > rawOffset
    if (endsAtOrAfterOffset) high = middle
    else low = middle + 1
  }

  const segment = segments[low]
  if (!segment || (isEnd ? rawOffset <= segment.start : rawOffset < segment.start)) return null
  return { node: segment.node, offset: rawOffset - segment.start, block: segment.block }
}

function lowerBound(values: number[], target: number): number {
  let low = 0
  let high = values.length
  while (low < high) {
    const middle = Math.floor((low + high) / 2)
    if (values[middle] < target) low = middle + 1
    else high = middle
  }
  return low
}

function nearestSanitizedBlockAncestor(node: Text): Element {
  let ancestor = node.parentElement
  while (ancestor && ancestor.localName !== 'body') {
    if (kSanitizedBlockTags.has(ancestor.localName)) return ancestor
    ancestor = ancestor.parentElement
  }
  return node.ownerDocument.body
}
